import React, { useState, useEffect } from 'react';
import { FaSave, FaSpinner, FaCheckCircle, FaExclamationCircle, FaTrash, FaPlus, FaTachometerAlt, FaCog, FaUserTie, FaArrowLeft, FaEdit, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { API_BASE, getImageUrl } from '../api';

/**
 * Modern Dashboard layout for LeoApply Admin Portal.
 */
const AdminPanel = () => {
  const [data, setData] = useState({ settings: {}, positions: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'settings', 'positions'
  const [isAuthenticated, setIsAuthenticated] = useState(localStorage.getItem('admin_auth') === 'true');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  const fetchData = React.useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/get_data.php`);
      const result = await response.json();
      if (result.status === 'success') {
        setData(result.data);
      } else {
        showMessage('error', result.message || 'Failed to load data.');
      }
    } catch (error) {
      showMessage('error', 'Network error. Make sure XAMPP is running and init_db.php was executed.');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleSettingChange = (key, value) => {
    setData(prev => ({
      ...prev,
      settings: { ...prev.settings, [key]: value }
    }));
  };

  const handlePositionChange = (index, field, value) => {
    const updatedPositions = [...data.positions];
    updatedPositions[index] = { ...updatedPositions[index], [field]: value };
    setData(prev => ({ ...prev, positions: updatedPositions }));
  };

  const saveDeadline = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/update_data.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update_deadline',
          deadline: data.settings.DEADLINE_DATE
        })
      });
      const result = await response.json();
      if (result.status === 'success') {
        showMessage('success', 'Deadline updated successfully!');
      } else {
        showMessage('error', result.message);
      }
    } catch (error) {
      showMessage('error', 'Failed to save deadline.');
    } finally {
      setSaving(false);
    }
  };

  const savePosition = async (position) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/update_data.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update_position',
          id: position.id,
          title: position.title,
          description: position.description,
          formUrl: position.formUrl,
          openDate: position.openDate,
          image: position.image
        })
      });
      const result = await response.json();
      if (result.status === 'success') {
        showMessage('success', `${position.title} updated successfully!`);
      } else {
        showMessage('error', result.message);
      }
    } catch (error) {
      showMessage('error', 'Failed to save position.');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;
    
    setUploadingImage(index);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch(`${API_BASE}/upload_image.php`, {
        method: 'POST',
        body: formData
      });
      const result = await response.json();
      
      if (result.status === 'success') {
        handlePositionChange(index, 'image', result.url);
        showMessage('success', 'Image uploaded! Remember to click Save.');
      } else {
        showMessage('error', result.message);
      }
    } catch (error) {
      showMessage('error', 'Failed to upload image.');
    } finally {
      setUploadingImage(null);
    }
  };

  const addPosition = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/update_data.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'add_position' })
      });
      const result = await response.json();
      if (result.status === 'success') {
        showMessage('success', 'New position created!');
        fetchData();
        setActiveTab('positions');
      } else {
        showMessage('error', result.message);
      }
    } catch (error) {
      showMessage('error', 'Failed to create new position.');
    } finally {
      setSaving(false);
    }
  };

  const deletePosition = async (id) => {
    if (!window.confirm("Are you sure you want to permanently delete this position?")) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/update_data.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete_position', id })
      });
      const result = await response.json();
      if (result.status === 'success') {
        showMessage('success', 'Position deleted successfully.');
        fetchData();
      } else {
        showMessage('error', result.message);
      }
    } catch (error) {
      showMessage('error', 'Failed to delete position.');
    } finally {
      setSaving(false);
    }
  };

  const movePosition = async (index, direction) => {
    if ((direction === -1 && index === 0) || (direction === 1 && index === data.positions.length - 1)) {
      return;
    }
    
    // Swap array items
    const newPositions = [...data.positions];
    const temp = newPositions[index];
    newPositions[index] = newPositions[index + direction];
    newPositions[index + direction] = temp;
    
    // Update local state immediately for snappy UI feel
    setData(prev => ({ ...prev, positions: newPositions }));
    
    // Save to DB silently
    setSaving(true);
    try {
      const orderData = newPositions.map((pos, i) => ({ id: pos.id, sort_order: i }));
      const response = await fetch(`${API_BASE}/update_data.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update_order', orderData })
      });
      const result = await response.json();
      if (result.status !== 'success') {
        showMessage('error', result.message);
        fetchData(); // Revert to DB state if failed
      }
    } catch (error) {
      showMessage('error', 'Failed to save new order.');
      fetchData();
    } finally {
      setSaving(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === '102030') {
      setIsAuthenticated(true);
      localStorage.setItem('admin_auth', 'true');
      setLoginError('');
    } else {
      setLoginError('Invalid password. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('admin_auth');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center p-6 font-sans">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 z-0" />
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#D4AF37]/10 rounded-full blur-[120px] z-0" />
        
        <div className="relative z-10 w-full max-w-md bg-white/5 border border-white/10 p-8 md:p-10 rounded-[2.5rem] backdrop-blur-xl shadow-2xl">
          <div className="flex flex-col items-center mb-10">
            <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#B8860B] rounded-2xl flex items-center justify-center text-black font-extrabold text-3xl mb-6 shadow-lg shadow-[#D4AF37]/20">
              L
            </div>
            <h1 className="text-3xl font-extrabold text-white mb-2">Admin Portal</h1>
            <p className="text-gray-400 font-medium">Enter password to access dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <input
                type="password"
                placeholder="Enter Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                className="w-full bg-white/5 border border-white/10 text-white rounded-2xl px-6 py-4 focus:ring-2 focus:ring-[#D4AF37]/50 focus:border-transparent outline-none transition-all placeholder:text-gray-600 font-medium text-center tracking-widest"
              />
              {loginError && <p className="text-red-400 text-sm mt-3 text-center font-bold animate-pulse">{loginError}</p>}
            </div>

            <button
              type="submit"
              className="w-full bg-[#D4AF37] hover:bg-[#FFD700] text-black font-extrabold py-4 rounded-2xl shadow-xl shadow-[#D4AF37]/10 transition-all active:scale-95"
            >
              Unlock Dashboard
            </button>
          </form>

          <div className="mt-10 text-center">
            <a href="/" className="text-gray-500 hover:text-white transition-colors text-sm font-bold flex items-center justify-center gap-2">
              <FaArrowLeft size={12} /> Back to live site
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <FaSpinner className="animate-spin text-4xl text-[#D4AF37]" />
      </div>
    );
  }

  // Calculate some stats for the overview
  const totalPositions = data.positions.length;
  const deadlineDate = new Date(data.settings.DEADLINE_DATE || 0);
  const isClosed = new Date() > deadlineDate;

  return (
    <div className="flex h-screen bg-[#000000] font-sans overflow-hidden text-gray-200">
      
      {/* ── Sidebar ── */}
      <aside className="w-64 bg-[#0a0a0a] text-gray-400 flex flex-col shadow-2xl z-20 hidden md:flex border-r border-white/5">
        <div className="p-6 bg-[#000000] border-b border-white/5 flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#B8860B] rounded-lg flex items-center justify-center text-black font-bold text-xl shadow-lg shadow-[#D4AF37]/10">
            L
          </div>
          <div>
            <h2 className="text-white font-bold tracking-wide">Leo Admin</h2>
            <p className="text-[10px] text-gray-500 uppercase tracking-widest">Dashboard</p>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          <button 
            onClick={() => setActiveTab('overview')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'overview' ? 'bg-[#D4AF37] text-black shadow-md' : 'hover:bg-white/5 hover:text-white'}`}
          >
            <FaTachometerAlt /> Overview
          </button>
          <button 
            onClick={() => setActiveTab('settings')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'settings' ? 'bg-[#D4AF37] text-black shadow-md' : 'hover:bg-white/5 hover:text-white'}`}
          >
            <FaCog /> Global Settings
          </button>
          <button 
            onClick={() => setActiveTab('positions')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'positions' ? 'bg-[#D4AF37] text-black shadow-md' : 'hover:bg-white/5 hover:text-white'}`}
          >
            <FaUserTie /> Board Positions
          </button>
        </nav>

        <div className="p-4 border-t border-white/5 space-y-2">
          <button 
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-950/20 hover:bg-red-900/30 text-red-400 rounded-xl transition-all text-sm font-bold border border-red-900/20"
          >
            Logout
          </button>
          <a href="/" className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white/5 hover:bg-white/10 text-white rounded-xl transition-all text-sm font-bold border border-white/5">
            <FaArrowLeft /> Back to Live Site
          </a>
        </div>
      </aside>

      {/* ── Main Content Area ── */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden relative">
        
        {/* Topbar Header */}
        <header className="bg-[#050505] px-8 py-5 shadow-sm border-b border-white/5 flex justify-between items-center z-10">
          <h1 className="text-2xl font-bold text-white capitalize">
            {activeTab.replace('_', ' ')}
          </h1>
          <div className="flex items-center gap-4">
             <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] flex items-center justify-center font-bold border border-[#D4AF37]/20">
               A
             </div>
          </div>
        </header>

        {/* Global Notifications Overlay */}
        {message && (
          <div className={`absolute top-6 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-xl shadow-2xl font-bold flex items-center gap-3 transition-all ${
            message.type === 'success' ? 'bg-green-600 text-white border border-green-700' : 'bg-red-600 text-white border border-red-700'
          }`}>
            {message.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
            {message.text}
          </div>
        )}

        {/* Scrollable Content View */}
        <div className="flex-1 overflow-y-auto p-6 md:p-10">
          <div className="max-w-5xl mx-auto">
            
            {/* ── OVERVIEW TAB ── */}
            {activeTab === 'overview' && (
              <div className="space-y-6 animate-fade-in">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Stat Card 1 */}
                  <div className="bg-white/5 p-6 rounded-2xl shadow-sm border border-white/5 flex flex-col items-center justify-center text-center">
                    <div className="w-14 h-14 bg-[#D4AF37]/10 text-[#D4AF37] rounded-full flex items-center justify-center text-2xl mb-4">
                      <FaUserTie />
                    </div>
                    <h3 className="text-3xl font-extrabold text-white">{totalPositions}</h3>
                    <p className="text-gray-400 font-medium">Active Roles</p>
                  </div>
                  
                  {/* Stat Card 2 */}
                  <div className={`bg-white/5 p-6 rounded-2xl shadow-sm border flex flex-col items-center justify-center text-center ${isClosed ? 'border-red-500/20' : 'border-green-500/20'}`}>
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl mb-4 ${isClosed ? 'bg-red-500/10 text-red-400' : 'bg-green-500/10 text-green-400'}`}>
                      <FaCheckCircle />
                    </div>
                    <h3 className="text-2xl font-extrabold text-white">{isClosed ? 'Closed' : 'Active'}</h3>
                    <p className="text-gray-400 font-medium">Portal Status</p>
                  </div>

                  {/* Stat Card 3 */}
                  <div className="bg-white/5 p-6 rounded-2xl shadow-sm border border-white/5 flex flex-col items-center justify-center text-center">
                    <button 
                      onClick={addPosition}
                      className="w-full h-full flex flex-col items-center justify-center text-[#D4AF37] hover:text-[#FFD700] transition-colors"
                    >
                      <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-full flex items-center justify-center text-2xl mb-4 border border-[#D4AF37]/20">
                        <FaPlus />
                      </div>
                      <p className="font-bold">Add New Role</p>
                    </button>
                  </div>
                </div>

                <div className="bg-white/5 p-8 rounded-2xl shadow-sm border border-white/5 mt-8 backdrop-blur-sm">
                  <h3 className="text-xl font-bold text-white mb-2">Welcome to the Dashboard!</h3>
                  <p className="text-gray-400 leading-relaxed">
                    Use the sidebar to navigate between Global Settings (where you can control the main countdown deadline) and Board Positions (where you can edit individual roles, forms, and their specific countdowns). All changes are instantly synced to the live website.
                  </p>
                </div>
              </div>
            )}

            {/* ── SETTINGS TAB ── */}
            {activeTab === 'settings' && (
              <div className="bg-white/5 p-8 rounded-2xl shadow-sm border border-white/5 animate-fade-in backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-8 pb-4 border-b border-white/5">
                  <div className="p-3 bg-white/5 text-[#D4AF37] rounded-xl"><FaCog size={24} /></div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Application Window</h2>
                    <p className="text-gray-400 text-sm">Control when the portal shuts down completely.</p>
                  </div>
                </div>
                
                <div className="max-w-xl">
                  <label className="block text-sm font-bold text-gray-400 mb-2">Global Deadline Date & Time</label>
                  <input
                    type="datetime-local"
                    value={data.settings.DEADLINE_DATE || ''}
                    onChange={(e) => handleSettingChange('DEADLINE_DATE', e.target.value)}
                    className="w-full bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-[#D4AF37]/50 focus:border-transparent block p-4 shadow-sm font-medium mb-6"
                  />
                  <button
                    onClick={saveDeadline}
                    disabled={saving}
                    className="w-full py-4 bg-[#D4AF37] hover:bg-[#FFD700] text-black font-bold rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-[#D4AF37]/10 transition disabled:opacity-70"
                  >
                    {saving ? <FaSpinner className="animate-spin" /> : <FaSave />} 
                    Save Deadline
                  </button>
                </div>
              </div>
            )}

            {/* ── POSITIONS TAB ── */}
            {activeTab === 'positions' && (
              <div className="animate-fade-in space-y-6">
                <div className="flex justify-between items-center bg-white/5 p-6 rounded-2xl shadow-sm border border-white/5 backdrop-blur-sm">
                  <div>
                    <h2 className="text-xl font-bold text-white">Manage Roles</h2>
                    <p className="text-gray-400 text-sm mt-1">Edit form links, descriptions, and open dates.</p>
                  </div>
                  <button
                    onClick={addPosition}
                    disabled={saving}
                    className="px-6 py-3 bg-[#D4AF37] hover:bg-[#FFD700] text-black font-bold rounded-xl flex items-center gap-2 shadow-lg shadow-[#D4AF37]/10 transition disabled:opacity-70"
                  >
                    <FaPlus /> New Role
                  </button>
                </div>
                
                <div className="grid grid-cols-1 gap-8">
                  {data.positions.map((pos, index) => (
                    <div key={pos.id} className="bg-white/5 rounded-3xl shadow-sm border border-white/5 overflow-hidden backdrop-blur-sm">
                      <div className="bg-white/5 px-6 py-4 border-b border-white/5 flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <FaEdit className="text-[#D4AF37]" />
                          <h3 className="font-bold text-white text-lg">Editing Role #{pos.id}</h3>
                        </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => movePosition(index, -1)}
                              disabled={saving || index === 0}
                              className="p-2 text-gray-400 hover:text-[#D4AF37] hover:bg-white/5 rounded-lg transition-colors disabled:opacity-30"
                              title="Move Up"
                            >
                              <FaArrowUp />
                            </button>
                            <button
                              onClick={() => movePosition(index, 1)}
                              disabled={saving || index === data.positions.length - 1}
                              className="p-2 text-gray-400 hover:text-[#D4AF37] hover:bg-white/5 rounded-lg transition-colors disabled:opacity-30"
                              title="Move Down"
                            >
                              <FaArrowDown />
                            </button>
                          
                          <div className="w-px h-6 bg-gray-200 mx-1"></div>
                          
                          <button
                            onClick={() => deletePosition(pos.id)}
                            disabled={saving}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Position"
                          >
                            <FaTrash size={18} />
                          </button>
                        </div>
                      </div>

                      <div className="p-6 md:p-8 flex flex-col lg:flex-row gap-8">
                        {/* Inputs Column */}
                        <div className="flex-1 space-y-5">
                          <div>
                            <label className="block text-sm font-bold text-gray-400 mb-1">Position Title</label>
                            <input
                              type="text"
                              value={pos.title}
                              onChange={(e) => handlePositionChange(index, 'title', e.target.value)}
                              className="w-full bg-white/5 border border-white/10 text-white rounded-xl p-3 focus:ring-2 focus:ring-[#D4AF37]/50 focus:border-transparent font-bold"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-bold text-gray-400 mb-1">Google Form URL</label>
                            <input
                              type="url"
                              value={pos.formUrl}
                              onChange={(e) => handlePositionChange(index, 'formUrl', e.target.value)}
                              className="w-full bg-white/5 border border-white/10 text-[#D4AF37] rounded-xl p-3 focus:ring-2 focus:ring-[#D4AF37]/50 focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-bold text-gray-400 mb-2">Image Banner</label>
                            <div className="flex items-center gap-4">
                              {pos.image && (
                                <img 
                                  src={getImageUrl(pos.image)} 
                                  alt="Preview" 
                                  className="w-20 h-14 object-cover rounded-lg border border-white/10 shadow-sm" 
                                />
                              )}
                              <input
                                type="file"
                                accept="image/png, image/jpeg, image/webp, image/gif"
                                onChange={(e) => handleImageUpload(index, e.target.files[0])}
                                disabled={uploadingImage === index}
                                className="w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-bold file:bg-[#D4AF37]/10 file:text-[#D4AF37] hover:file:bg-[#D4AF37]/20 transition-all cursor-pointer"
                              />
                            </div>
                            {uploadingImage === index && <p className="text-xs text-[#D4AF37] mt-2 font-bold animate-pulse flex items-center gap-1"><FaSpinner className="animate-spin" /> Uploading image...</p>}
                          </div>

                          <div>
                            <label className="block text-sm font-bold text-gray-400 mb-1">Applications Open Date (Countdown)</label>
                            <input
                              type="datetime-local"
                              value={pos.openDate}
                              onChange={(e) => handlePositionChange(index, 'openDate', e.target.value)}
                              className="w-full bg-white/5 border border-white/10 text-white rounded-xl p-3 focus:ring-2 focus:ring-[#D4AF37]/50 focus:border-transparent"
                            />
                          </div>
                        </div>

                        {/* Textarea & Save Column */}
                        <div className="flex-1 flex flex-col">
                          <label className="block text-sm font-bold text-gray-400 mb-1">Description</label>
                          <textarea
                            value={pos.description}
                            onChange={(e) => handlePositionChange(index, 'description', e.target.value)}
                            rows="5"
                            className="w-full flex-1 bg-white/5 border border-white/10 text-white rounded-xl p-4 focus:ring-2 focus:ring-[#D4AF37]/50 focus:border-transparent resize-none leading-relaxed mb-5"
                          />
                          
                          <button
                            onClick={() => savePosition(pos)}
                            disabled={saving}
                            className="w-full py-3.5 bg-[#D4AF37] hover:bg-[#FFD700] text-black font-bold rounded-xl flex items-center justify-center gap-2 shadow-md transition disabled:opacity-70"
                          >
                            {saving ? <FaSpinner className="animate-spin" /> : <FaSave />} 
                            Save {pos.title}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </main>
      
    </div>
  );
};

export default AdminPanel;
